/** Input tab — the run's stored input document.
 *
 * All rendering (format detection, pretty-print, wrap, copy, download) lives
 * in the shared `DocumentViewer`; this tab only fetches. Feed documents arrive
 * minified more often than not, so the viewer pretty-prints by default while
 * Copy/Download still emit the stored bytes — which is what the Reimport tab
 * resubmits.
 */
import { Alert, Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as importRunsApi from '@/api/importRuns';
import { DocumentViewer } from '@/components/DocumentViewer';

export function InputTab({ runId }: { runId: string }) {
  const query = useQuery({
    queryKey: ['import-runs', 'input', runId],
    queryFn: () => importRunsApi.inputData(runId),
    staleTime: Infinity, // the stored input never changes
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return <Alert severity="error">Couldn’t load the input document.</Alert>;
  }

  return (
    <DocumentViewer
      text={query.data.input_data ?? ''}
      formatHint={query.data.format}
      downloadName={`import-${runId}-input`}
      copyMessage="Input copied to clipboard."
      emptyMessage="No input document is stored for this run."
    />
  );
}
